<script lang="ts" setup>
import type { ComponentElementsLinkWrapper } from '@autobid/strapi-integration/graphql/graphql'
import { useAutobidAuth } from '@autobid/nuxt-auth/src/composables/useAutobidAuth'
import { useNotificationStore } from '@autobid/nuxt-pinia-store/store/useNotificationStore'
import { useNotificationsSocket } from '@autobid/ui/composables/useNotificationsSocket'
import { NOTIFICATION_ICONS } from '@autobid/ui/constants/NOTIFICATION_ICONS'
import { MenuItem } from '@headlessui/vue'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import Notification from './Notification.vue'
import NotificationsButton from './NotificationsButton.vue'
import NotificationsLoadMore from './NotificationsLoadMore.vue'
import NotificationsMarkAsRead from './NotificationsMarkAsRead.vue'

interface Props {
  data: ComponentElementsLinkWrapper
}

defineProps<Props>()

const { isAuthed } = useAutobidAuth()
const {
  notifications,
  amountOfNotificationsToSync,
  templates,
  fetchingFailed,
  loading,
} = storeToRefs(useNotificationStore())
const { getNotifications } = useNotificationStore()

useNotificationsSocket()

const unreadNotificationQty = computed(
  () =>
    notifications.value.filter(el => Number(el.status) === 0).length
    + amountOfNotificationsToSync.value,
)

function deferGetNotifications() {
  // defer the request; sometime there is an error when there is a lot of request at once e.g. at the contact form
  setTimeout(() => {
    getNotifications({ limit: 50, offset: 0 })
  }, 300)
}

const ADD_HOME_SCREEN_LC_KEY = 'showInfoAboutAddingToHomeScreen'
const showInfoAboutAddingToHomeScreen = ref(false)
function checkInfo() {
  if (!process.client || !isAuthed.value) {
    return
  }
  if (typeof window.Notification !== 'undefined')
    return
  const setInfoToLocalStorage = () => {
    localStorage.setItem(
      ADD_HOME_SCREEN_LC_KEY,
      JSON.stringify({ expiresAt: Date.now() + 1000 * 60 * 60 * 24 * 7 }),
    )
  }
  const stringInfoData = localStorage.getItem(ADD_HOME_SCREEN_LC_KEY)
  if (stringInfoData) {
    try {
      const infoData = JSON.parse(stringInfoData)
      if (infoData.expiresAt && infoData.expiresAt < Date.now()) {
        showInfoAboutAddingToHomeScreen.value = true
        setInfoToLocalStorage()
      }
      return
    }
    catch {
      // ignore
    }
  }
  showInfoAboutAddingToHomeScreen.value = true
  setInfoToLocalStorage()
}

async function handleOpen() {
  if (!amountOfNotificationsToSync.value)
    return

  await getNotifications({ offset: 0, limit: 50 }, true)

  amountOfNotificationsToSync.value = 0
}

onMounted(() => {
  if (isAuthed.value) {
    deferGetNotifications()
  }

  checkInfo()
})
</script>

<template>
  <common-dropdown
    v-if="isAuthed"
    x="right"
    max-height="min(700px, calc(95vh - 64px))"
  >
    <template #button>
      <span class="flex items-center" @click="handleOpen">
        <NotificationsButton :amount="unreadNotificationQty" :icon="data.icon ? String(data.icon) : undefined">
          {{ data.text }}
        </NotificationsButton>
      </span>
    </template>

    <template #items>
      <NotificationsMarkAsRead v-if="unreadNotificationQty" />

      <div v-if="fetchingFailed" class="sticky top-0 z-50 bg-white px-2 pt-2">
        <p
          class="block min-w-[200px] max-w-[300px] whitespace-normal text-red sm:min-w-0 sm:whitespace-nowrap"
        >
          {{ $t('header.notifications-center.loading-notifications-error') }}
        </p>
        <ElementsButton
          class="my-2 ml-auto text-sm !text-onPrimary"
          text-color="primary"
          size="tiny"
          :disabled="loading.notifications"
          @click="
            getNotifications(
              { offset: 0, limit: notifications.length || 10 },
              true,
            )
          "
        >
          {{ $t('header.notifications-center.reload') }}
          <Icon
            v-if="loading.notifications"
            mode="svg"
            name="eos-icons:loading"
            class="ml-1 h-4 w-4"
          />
        </ElementsButton>
      </div>

      <Notification
        v-for="notification in notifications"
        :key="notification.id"
        :notification="notification"
      >
        <div class="flex items-center">
          <Icon
            v-if="templates[Number(notification.type)]"
            mode="svg"
            :name="NOTIFICATION_ICONS[templates[Number(notification.type)].style]"
            class="mr-1 h-6 w-6 min-w-[24px]"
          />

          <span class="flex flex-col">
            {{ templates[Number(notification.type)]?.body ?? '' }}

            <span v-if="notification.car" class="opacity-60">
              {{ notification.car.name }}
            </span>

            <span class="opacity-80">
              {{ notification.timestamp }}
            </span>
          </span>
        </div>
      </Notification>

      <NotificationsLoadMore />

      <MenuItem
        v-if="!notifications.length && !fetchingFailed"
        disabled
      >
        <span
          class="block p-2 text-primary"
        >
          {{ $t('header.notifications-center.lack-of-notifications') }}
        </span>
      </MenuItem>
    </template>
  </common-dropdown>

  <LazyCommonDialogAddToHomeScreen
    v-if="showInfoAboutAddingToHomeScreen && isAuthed"
    :opened="showInfoAboutAddingToHomeScreen"
    @close="showInfoAboutAddingToHomeScreen = false"
  />
</template>
